import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { TestCardStatus, TestType } from "@app/provider/testData/types";
import { routes } from "@app/routing";
import { Grid, GridCell } from "@natera/material/lib/layout";
import {
  AppointmentDetails,
  DetailInformationButton,
  InformationCard,
  KitDetailsSection,
  SpinnerView,
  TestCard,
  TestInformationCard,
  MyResultsSection,
} from "@app/components";
import { BlueHeaderContent } from "@app/components/ui/layout";
import InformationIcon from "@assets/svg/icons/information.svg";
import LinkArrowIcon from "@assets/svg/icons/link-arrow.svg";
import { messages } from "./messages";
import { TestDetailsContext } from "@app/provider/testData";
import { showAppointmentDetails } from "@app/utils";
import { getReturnRouteFromOrigin } from "@app/utils/navigationOrigin";
import { useLocation, useNavigate } from "react-router-dom";
import { BusinessUnits } from "@app/provider/drawRequest";

import "./detailsRecord.scss";

const DetailRecordPage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading, getTestDetails, getOrderUidPublic, getTestUid } =
    useContext(TestDetailsContext);

  const testDetails = getTestDetails();
  const orderUidPublic = getOrderUidPublic();
  const testUid = getTestUid();

  const content = useMemo(
    () =>
      testDetails ? (
        <TestInformationCard testDetailsInformation={testDetails} />
      ) : null,
    [testDetails]
  );

  const [isKitSectionActive, setKitSectionActive] = useState(false);
  const [isAppointmentDetailsNeeded, setIsAppointmentDetailsNeeded] =
    useState(false);
  const [areMyResultsReady, setMyResultsReady] = useState(false);

  const kitSectionAllowedStatuses = [
    TestCardStatus.KIT_SHIPPED,
    TestCardStatus.APPT_CONFIRMED,
    TestCardStatus.APPT_REQUESTED,
  ];

  const extendKitSectionAllowedStatuses = (statuses: TestCardStatus[]) => {
    statuses.forEach((status) => {
      if (!kitSectionAllowedStatuses.includes(status)) {
        kitSectionAllowedStatuses.push(status);
      }
    });
  };

  useEffect(() => {
    if (testDetails?.businessUnit === BusinessUnits.ONCOLOGY) {
      extendKitSectionAllowedStatuses([
        TestCardStatus.TEST_ORDERED,
        TestCardStatus.UPCOMING_TEST,
      ]);
    } else if (
      testDetails?.businessUnit === BusinessUnits.WOMENS_HEALTH &&
      testDetails?.testType === TestType.EMPOWER
    ) {
      extendKitSectionAllowedStatuses([TestCardStatus.TEST_ORDERED]);
    }

    if (testDetails && kitSectionAllowedStatuses.includes(testDetails.status)) {
      setKitSectionActive(true);
    }

    if (
      testDetails &&
      [TestCardStatus.RESULT_READY, TestCardStatus.TEST_COMPLETE].includes(
        testDetails.status
      )
    ) {
      setMyResultsReady(true);
    }

    const isShowAppointmentDetails = showAppointmentDetails(testDetails);
    setIsAppointmentDetailsNeeded(isShowAppointmentDetails);

    if (
      testDetails &&
      testDetails.testType === TestType.SINGLE_GENE_NIPT &&
      kitSectionAllowedStatuses.includes(testDetails.status)
    ) {
      setKitSectionActive(true);
    }
  }, [testDetails]);

  const handleModifyAppointment = () => {
    const baseState =
      location.state && typeof location.state === "object"
        ? (location.state as Record<string, unknown>)
        : {};
    navigate(routes.sampleDrawModifyPage(orderUidPublic, testUid), {
      state: { ...baseState, prevPage: location.pathname },
    });
  };

  const handleScheduleDraw = () => {
    navigate(routes.sampleDrawPage(orderUidPublic, testUid), {
      state: location.state,
    });
  };

  const backRoute = getReturnRouteFromOrigin(location.state, routes.home);

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.detailsRecordBackButton)}
      backArrowHeaderRoute={backRoute}
    >
      <div className="details-record">
        <Grid>
          <SpinnerView isLoading={isLoading} />
          {testDetails && (
            <GridCell className="details-record__test-cards" span={12}>
              <div className="details-record__column">
                <TestCard testCard={testDetails} showPrimaryAction={false} />
                <div className="details-record__wrapper">
                  <div className="details-record__test-information-button">
                    <DetailInformationButton
                      name={intl.formatMessage(
                        messages.detailsRecordTestInformationButton
                      )}
                      route={routes.testDetailsInformationPage(
                        testDetails.orderUidPublic,
                        testDetails.uid
                      )}
                      linkState={location.state}
                      startIcon={InformationIcon}
                      endIcon={LinkArrowIcon}
                    />
                  </div>

                  {isKitSectionActive &&
                    testDetails?.trackingNumber &&
                    testDetails?.kitShippedDate && (
                      <div className="details-record__kit-details">
                        <KitDetailsSection
                          trackingNumber={testDetails.trackingNumber}
                          kitShippedDate={testDetails.kitShippedDate}
                        />
                      </div>
                    )}

                  {isAppointmentDetailsNeeded && (
                    <div className="details-record__appointment-details">
                      <AppointmentDetails
                        handleModifyAppointment={handleModifyAppointment}
                        handleScheduleDraw={handleScheduleDraw}
                      />
                    </div>
                  )}

                  {areMyResultsReady && (
                    <div className="details-record__view-results">
                      <MyResultsSection testCard={testDetails} />
                    </div>
                  )}
                </div>
              </div>
              <div className="details-record__test-information-card">
                <InformationCard
                  headerIcon={InformationIcon}
                  headerText={intl.formatMessage(
                    messages.detailsRecordTestInformationHeader
                  )}
                  content={content}
                />
              </div>
            </GridCell>
          )}
        </Grid>
      </div>
    </BlueHeaderContent>
  );
};

export default DetailRecordPage;
